import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, Plus, Save, Eye } from "lucide-react";
import { api, formatApiError, assetUrl } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { Seo } from "@/components/Seo";

const TYPES = [
  { v: "software_review", l: "Software Review" },
  { v: "product_review", l: "Product Review" },
  { v: "comparison", l: "Head-to-Head Comparison" },
  { v: "buying_guide", l: "Best Of / Buying Guide" },
  { v: "guide", l: "Property Management Guide" },
];

const EMPTY = {
  type: "software_review", title: "", slug: "", excerpt: "", meta_title: "", meta_description: "",
  body: "", category: "", status: "draft", scheduled_at: "", featured_image: "", featured_image_alt: "",
  author: "Ramon Pasos", product_name: "", product_logo: "", product_logo_alt: "", pricing: "", best_for: "",
  strengths: [], weaknesses: [], key_features: [], screenshots: [], final_verdict: "", scores: {},
  affiliate_link_id: "", compare_slugs: [],
};

const REVIEW_TYPES = ["software_review", "product_review"];

export default function ArticleEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!editing);

  useEffect(() => {
    api.get("/scoring-config").then((r) => setCategories(r.data.categories));
    api.get("/admin/affiliate-links").then((r) => setAffiliates(r.data));
    api.get("/admin/articles").then((r) => setAllArticles(r.data));
    if (editing) {
      api.get(`/admin/articles/${id}`).then((r) => {
        setForm({ ...EMPTY, ...r.data, affiliate_link_id: r.data.affiliate_link_id || "", scheduled_at: r.data.scheduled_at || "" });
        setLoaded(true);
      }).catch(() => { toast.error("Failed to load"); nav("/admin"); });
    }
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setScore = (key, v) => setForm((f) => ({ ...f, scores: { ...f.scores, [key]: v === "" ? null : Number(v) } }));

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const save = async (overrideStatus) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (overrideStatus) payload.status = overrideStatus;
      payload.affiliate_link_id = payload.affiliate_link_id || null;
      payload.scheduled_at = payload.scheduled_at || null;
      // strip empty scores keys but keep nulls out
      const cleanScores = {};
      Object.entries(payload.scores || {}).forEach(([k, v]) => { if (v != null && v !== "") cleanScores[k] = Number(v); });
      payload.scores = cleanScores;
      let res;
      if (editing) res = await api.put(`/admin/articles/${id}`, payload);
      else res = await api.post("/admin/articles", payload);
      toast.success(editing ? "Saved" : "Created");
      if (!editing) nav(`/admin/articles/${res.data.id}`);
      else setForm((f) => ({ ...f, ...res.data, affiliate_link_id: res.data.affiliate_link_id || "" }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const isReview = REVIEW_TYPES.includes(form.type);
  const isComparison = form.type === "comparison";

  if (!loaded) return <AdminShell title="Loading…"><div className="font-mono-pm text-sm text-stone-500">Loading…</div></AdminShell>;

  return (
    <AdminShell title={editing ? "Edit Article" : "New Article"} actions={
      <div className="flex items-center gap-2">
        {editing && <a href={`${isComparison ? "/comparisons" : "/reviews"}/${form.slug}?preview=1`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-stone-900 px-4 py-2.5 font-mono-pm text-xs uppercase hover:bg-stone-100"><Eye size={14} /> Preview</a>}
        <button onClick={() => save()} disabled={saving} data-testid="save-article" className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 font-mono-pm text-xs tracking-widest uppercase hover:bg-[#991B1B] disabled:opacity-50"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
      </div>}>
      <Seo title="Article Editor" noindex />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* MAIN COLUMN */}
        <div className="lg:col-span-2 space-y-6">
          <Field label="Title">
            <input data-testid="article-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="DoorLoop Review: What Property Managers Actually Need to Know" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Content Type">
              <select data-testid="article-type" value={form.type} onChange={(e) => set("type", e.target.value)} className={inputCls}>
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <input value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} placeholder="Software, Maintenance & Work Orders…" />
            </Field>
          </div>
          <Field label="Excerpt / Summary">
            <textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className={inputCls} placeholder="One-sentence summary shown on cards and search results." />
          </Field>

          {(isReview) && (
            <div className="border border-stone-900 bg-white p-5 space-y-5">
              <h3 className="font-mono-pm text-xs tracking-widest uppercase text-[#991B1B]">Review Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Product Name"><input value={form.product_name} onChange={(e) => set("product_name", e.target.value)} className={inputCls} /></Field>
                <Field label="Pricing"><input value={form.pricing} onChange={(e) => set("pricing", e.target.value)} className={inputCls} placeholder="From $59/mo" /></Field>
              </div>
              <Field label="Best For"><input value={form.best_for} onChange={(e) => set("best_for", e.target.value)} className={inputCls} placeholder="Small-to-mid residential portfolios" /></Field>
              <ImageField label="Product Logo" url={form.product_logo} alt={form.product_logo_alt} onUpload={async (f) => set("product_logo", await upload(f))} onAlt={(v) => set("product_logo_alt", v)} onClear={() => set("product_logo", "")} />
              <ListField label="Strengths" items={form.strengths} onChange={(v) => set("strengths", v)} />
              <ListField label="Weaknesses" items={form.weaknesses} onChange={(v) => set("weaknesses", v)} />
              <ListField label="Key Features" items={form.key_features} onChange={(v) => set("key_features", v)} />
              <Field label="Final Verdict"><textarea value={form.final_verdict} onChange={(e) => set("final_verdict", e.target.value)} rows={3} className={inputCls} placeholder="Our bottom-line take…" /></Field>
              <ScreenshotsField screenshots={form.screenshots} onChange={(v) => set("screenshots", v)} upload={upload} />
            </div>
          )}

          {isComparison && (
            <div className="border border-stone-900 bg-white p-5">
              <h3 className="font-mono-pm text-xs tracking-widest uppercase text-[#991B1B] mb-4">Products to Compare (2–4)</h3>
              <p className="font-mono-pm text-[11px] text-stone-500 mb-3">Select existing review articles. Their scores populate the comparison table automatically.</p>
              <div className="space-y-2">
                {allArticles.filter((a) => REVIEW_TYPES.includes(a.type)).map((a) => (
                  <label key={a.slug} className="flex items-center gap-3 border border-stone-300 p-3 cursor-pointer hover:bg-stone-50">
                    <input type="checkbox" checked={form.compare_slugs.includes(a.slug)} className="w-4 h-4 accent-stone-900"
                      onChange={(e) => {
                        const next = e.target.checked ? [...form.compare_slugs, a.slug] : form.compare_slugs.filter((s) => s !== a.slug);
                        set("compare_slugs", next.slice(0, 4));
                      }} />
                    <span className="text-sm text-stone-800">{a.product_name || a.title}</span>
                    <span className="font-mono-pm text-[10px] text-stone-400 ml-auto">{a.is_scored ? a.overall_score : "unscored"}</span>
                  </label>
                ))}
                {allArticles.filter((a) => REVIEW_TYPES.includes(a.type)).length === 0 && <p className="font-mono-pm text-xs text-stone-400">Create review articles first to compare them.</p>}
              </div>
            </div>
          )}

          <Field label="Article Body">
            <RichTextEditor value={form.body} onChange={(v) => set("body", v)} />
          </Field>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-6">
          <div className="border border-stone-900 bg-white p-5 space-y-4">
            <h3 className="font-mono-pm text-xs tracking-widest uppercase text-stone-500">Publish</h3>
            <Field label="Status">
              <select data-testid="article-status" value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </Field>
            {form.status === "scheduled" && (
              <Field label="Scheduled Date"><input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} className={inputCls} /></Field>
            )}
            <div className="flex flex-col gap-2 pt-2">
              <button onClick={() => save("draft")} disabled={saving} className="w-full border border-stone-900 py-2.5 font-mono-pm text-xs uppercase tracking-widest hover:bg-stone-100">Save as Draft</button>
              <button onClick={() => save("published")} disabled={saving} data-testid="publish-btn" className="w-full bg-stone-900 text-white py-2.5 font-mono-pm text-xs uppercase tracking-widest hover:bg-[#991B1B]">Publish</button>
            </div>
          </div>

          {isReview && (
            <div className="border border-stone-900 bg-white p-5">
              <h3 className="font-mono-pm text-xs tracking-widest uppercase text-stone-500 mb-1">PM Test Score</h3>
              <p className="font-mono-pm text-[10px] text-stone-400 mb-4">Leave blank until reviewed. Overall score computes only when all nine are filled.</p>
              <div className="space-y-3">
                {categories.map((c) => (
                  <div key={c.key} className="flex items-center gap-3">
                    <label className="font-mono-pm text-[11px] text-stone-700 flex-1 leading-tight">{c.label}</label>
                    <input type="number" min="0" max="10" step="0.5" data-testid={`score-${c.key}`}
                      value={form.scores?.[c.key] ?? ""} onChange={(e) => setScore(c.key, e.target.value)}
                      className="w-16 border border-stone-900 px-2 py-1.5 text-center font-mono-pm" placeholder="—" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {isReview && (
            <div className="border border-stone-900 bg-white p-5">
              <h3 className="font-mono-pm text-xs tracking-widest uppercase text-stone-500 mb-3">Affiliate Link</h3>
              <select value={form.affiliate_link_id} onChange={(e) => set("affiliate_link_id", e.target.value)} className={inputCls}>
                <option value="">— None —</option>
                {affiliates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}

          <div className="border border-stone-900 bg-white p-5 space-y-4">
            <h3 className="font-mono-pm text-xs tracking-widest uppercase text-stone-500">Featured Image</h3>
            <ImageField url={form.featured_image} alt={form.featured_image_alt} onUpload={async (f) => set("featured_image", await upload(f))} onAlt={(v) => set("featured_image_alt", v)} onClear={() => set("featured_image", "")} />
          </div>

          <div className="border border-stone-900 bg-white p-5 space-y-4">
            <h3 className="font-mono-pm text-xs tracking-widest uppercase text-stone-500">SEO</h3>
            <Field label="URL Slug"><input value={form.slug} onChange={(e) => set("slug", e.target.value)} className={inputCls} placeholder="auto-generated from title" /></Field>
            <Field label="Meta Title"><input value={form.meta_title} onChange={(e) => set("meta_title", e.target.value)} className={inputCls} /></Field>
            <Field label="Meta Description"><textarea value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} rows={3} className={inputCls} /></Field>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

const inputCls = "w-full border border-stone-900 px-3 py-2.5 bg-white font-sans-pm text-sm focus:outline-none";

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono-pm text-[11px] tracking-widest uppercase text-stone-600 block mb-2">{label}</label>
      {children}
    </div>
  );
}

function ListField({ label, items, onChange }) {
  const [val, setVal] = useState("");
  const add = () => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } };
  return (
    <Field label={label}>
      <div className="flex gap-2 mb-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className={inputCls} placeholder={`Add a ${label.toLowerCase().replace(/s$/, "")}…`} />
        <button type="button" onClick={add} className="bg-stone-900 text-white px-4 shrink-0"><Plus size={16} /></button>
      </div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 border border-stone-200 bg-stone-50 px-3 py-2">
            <span className="text-sm text-stone-800 flex-1">{it}</span>
            <button type="button" onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-stone-400 hover:text-[#991B1B]"><X size={14} /></button>
          </div>
        ))}
      </div>
    </Field>
  );
}

function ImageField({ label, url, alt, onUpload, onAlt, onClear }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { await onUpload(f); } catch (err) { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div>
      {label && <label className="font-mono-pm text-[11px] tracking-widest uppercase text-stone-600 block mb-2">{label}</label>}
      {url ? (
        <div className="border border-stone-300 p-3 bg-stone-50">
          <img src={assetUrl(url)} alt={alt || ""} className="max-h-32 border border-stone-300 bg-white" />
          <button type="button" onClick={onClear} className="mt-2 font-mono-pm text-[10px] uppercase text-[#991B1B] hover:underline">Remove</button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current.click()} disabled={busy} className="w-full border-2 border-dashed border-stone-400 py-6 flex flex-col items-center gap-2 hover:bg-stone-50 text-stone-500">
          <Upload size={20} /><span className="font-mono-pm text-xs uppercase">{busy ? "Uploading…" : "Upload image"}</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} />
      <input value={alt || ""} onChange={(e) => onAlt(e.target.value)} placeholder="Alt text (for SEO & accessibility)" className={`${inputCls} mt-2`} />
    </div>
  );
}

function ScreenshotsField({ screenshots, onChange, upload }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const url = await upload(f); onChange([...screenshots, { url, alt: "" }]); } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <Field label="Screenshots">
      <div className="space-y-3">
        {screenshots.map((s, i) => (
          <div key={i} className="flex gap-3 border border-stone-200 p-3 bg-stone-50">
            <img src={assetUrl(s.url)} alt="" className="w-24 h-16 object-cover border border-stone-300" />
            <input value={s.alt} onChange={(e) => onChange(screenshots.map((x, xi) => xi === i ? { ...x, alt: e.target.value } : x))} placeholder="Alt text / caption" className={`${inputCls} flex-1`} />
            <button type="button" onClick={() => onChange(screenshots.filter((_, xi) => xi !== i))} className="text-stone-400 hover:text-[#991B1B]"><X size={16} /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => ref.current.click()} disabled={busy} className="mt-2 w-full border-2 border-dashed border-stone-400 py-4 flex items-center justify-center gap-2 hover:bg-stone-50 text-stone-500 font-mono-pm text-xs uppercase">
        <Upload size={16} /> {busy ? "Uploading…" : "Add screenshot"}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={add} />
    </Field>
  );
}
